import { useState, useEffect } from "react";
import { ScrollView, Pressable, ActivityIndicator, Alert } from "react-native";
import styled from "styled-components/native";
import { useRouter } from "expo-router";
import { httpsCallable, getFunctions } from "firebase/functions";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useMembers } from "@/hooks/useMembers";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";

const functions = getFunctions(undefined, "europe-west2");

const Header = styled.View`
  padding: 20px;
`;

const Title = styled.Text`
  font-size: 20px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  margin-bottom: 4px;
`;

const Subtitle = styled.Text`
  font-size: 13px;
  color: ${colors.inkMuted};
  line-height: 20px;
`;

const WarningBanner = styled.View`
  background-color: ${colors.amber50};
  border: 1px solid ${colors.amber200};
  border-radius: ${radius.md}px;
  padding: 12px 14px;
  margin: 0 20px 16px;
`;

const BannerText = styled.Text`
  font-size: 13px;
  font-weight: ${font.bold};
  color: ${colors.amber};
  text-align: center;
`;

const ReturnBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.md}px;
  padding: 10px 16px;
  align-items: center;
  margin-top: 8px;
`;

const ReturnBtnText = styled.Text`
  color: #fff;
  font-size: 13px;
  font-weight: ${font.bold};
`;

const Card = styled.View`
  margin: 0 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  overflow: hidden;
`;

const Row = styled.View<{ isActive?: boolean; bordered?: boolean }>`
  flex-direction: row;
  align-items: center;
  padding: 14px 16px;
  background-color: ${(p) => (p.isActive ? colors.brand50 : colors.white)};
  ${(p) =>
    p.bordered
      ? `border-bottom-width: 1px; border-bottom-color: ${colors.line};`
      : ""}
`;

const Avatar = styled.View`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background-color: ${colors.brandLight};
  align-items: center;
  justify-content: center;
  margin-right: 12px;
`;

const AvatarText = styled.Text`
  font-size: 14px;
  font-weight: ${font.extrabold};
  color: ${colors.brandDark};
`;

const MemberInfo = styled.View`
  flex: 1;
`;

const MemberName = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const MemberDetail = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const ActiveBadge = styled.View`
  background-color: ${colors.brand};
  border-radius: 999px;
  padding: 3px 10px;
`;

const ActiveBadgeText = styled.Text`
  color: #fff;
  font-size: 11px;
  font-weight: ${font.bold};
`;

const SwitchingBadge = styled.View`
  background-color: ${colors.amber};
  border-radius: 999px;
  padding: 3px 10px;
`;

const SwitchingBadgeText = styled.Text`
  color: #fff;
  font-size: 11px;
  font-weight: ${font.bold};
`;

const LoadingContainer = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

const Spacer = styled.View`
  height: 32px;
`;

const SwitchingOverlay = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.92);
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

const SwitchingText = styled.Text`
  font-size: 15px;
  font-weight: ${font.bold};
  color: ${colors.ink};
  margin-top: 12px;
`;

const ADMIN_EMAIL = "veer25mangat@gmail.com";

export default function DevSwitcherScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { club } = useClub();
  const { members, loading } = useMembers(club?.id);

  const isCurrentUserAdmin = user?.email?.toLowerCase() === ADMIN_EMAIL;
  const isTestUser = user?.uid?.startsWith("test-player-");
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [switchingAdmin, setSwitchingAdmin] = useState(false);
  const isSwitching = !!switchingId || switchingAdmin;

  // Reset switching state when user changes
  useEffect(() => {
    setSwitchingId(null);
    setSwitchingAdmin(false);
  }, [user?.uid]);

  const handleSwitchToTestUser = async (
    userId: string,
    displayName: string,
  ) => {
    setSwitchingId(userId);
    try {
      const devSignIn = httpsCallable(functions, "devSignIn");
      const result = await devSignIn({ userId });
      const { token } = result.data as { token: string };
      await signInWithCustomToken(auth, token);
    } catch (err: any) {
      console.error("Dev sign-in error:", err);
    }
  };

  const handleReturnToAdmin = async () => {
    setSwitchingAdmin(true);
    try {
      const devSignIn = httpsCallable(functions, "devSignIn");
      const result = await devSignIn({ returnToAdmin: true });
      const { token } = result.data as { token: string };
      await signInWithCustomToken(auth, token);
    } catch (err: any) {
      console.error("Return to admin error:", err);
      await auth.signOut();
    }
  };

  const testMembers = members.filter((m) =>
    m.userId.startsWith("test-player-"),
  );
  const isCurrentlyTestUser = isTestUser;

  return (
    <>
      <ScrollView
        style={{ flex: 1, backgroundColor: colors.bg }}
        showsVerticalScrollIndicator={false}
      >
        <ContentWidth>
          <Header>
            <Title>Dev: Switch User</Title>
            <Subtitle>
              Sign in as a test player to see the app from their perspective.
              This is a real sign-in — permissions, channels, everything works
              as that user.
            </Subtitle>
          </Header>

          {isCurrentlyTestUser && (
            <WarningBanner>
              <BannerText>
                Signed in as test user: {user?.displayName || user?.uid}
              </BannerText>
              <Pressable onPress={handleReturnToAdmin}>
                <ReturnBtn>
                  <ReturnBtnText>Return to admin account</ReturnBtnText>
                </ReturnBtn>
              </Pressable>
            </WarningBanner>
          )}

          {loading ? (
            <LoadingContainer>
              <ActivityIndicator size="large" color={colors.brand} />
            </LoadingContainer>
          ) : (
            <Card>
              {testMembers.map((m, i) => {
                const isActive = user?.uid === m.userId;
                const isSwitching = switchingId === m.userId;
                return (
                  <Pressable
                    key={m.id}
                    onPress={() =>
                      !isActive &&
                      !switchingId &&
                      handleSwitchToTestUser(m.userId, m.displayName)
                    }
                    disabled={isActive || !!switchingId}
                  >
                    <Row
                      isActive={isActive || isSwitching}
                      bordered={i < testMembers.length - 1}
                    >
                      <Avatar>
                        <AvatarText>
                          {m.displayName?.[0]?.toUpperCase() || "?"}
                        </AvatarText>
                      </Avatar>
                      <MemberInfo>
                        <MemberName>{m.displayName}</MemberName>
                        <MemberDetail>
                          {m.role} · {m.email}
                        </MemberDetail>
                      </MemberInfo>
                      {isSwitching && (
                        <SwitchingBadge>
                          <SwitchingBadgeText>Switching...</SwitchingBadgeText>
                        </SwitchingBadge>
                      )}
                      {isActive && !isSwitching && (
                        <ActiveBadge>
                          <ActiveBadgeText>Signed in</ActiveBadgeText>
                        </ActiveBadge>
                      )}
                    </Row>
                  </Pressable>
                );
              })}
            </Card>
          )}

          <Spacer />
        </ContentWidth>
      </ScrollView>
      {isSwitching && (
        <SwitchingOverlay>
          <ActivityIndicator size="large" color={colors.brand} />
          <SwitchingText>Switching account...</SwitchingText>
        </SwitchingOverlay>
      )}
    </>
  );
}
